import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Minus, Pause, Play, Plus, RotateCcw, SkipForward } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useStandort } from '@/components/layouts/use-standort';
import { paymentsApi, type CashflowEvent, type CashflowForecast } from '@/lib/api/payments';
import { todayIso } from '@/lib/billing';

// Tempo wie in Tobis Paket-Simulation: Millisekunden je Tag, 140 ms = 1,0×.
const TEMPO = [500, 350, 250, 180, 140, 110, 85, 65, 45, 30];
const NORMALTEMPO = 140;

const FARBE = {
  kontostand: '#16a34a',
  verbindlichkeit: '#dc2626',
  auszahlbar: '#2563eb',
  auszahlung: '#7c3aed',
};

const PUNKT: Record<CashflowEvent['kind'], string> = {
  income: 'bg-green-600',
  payout: 'bg-violet-600',
  lessons: 'bg-blue-600',
  package: 'bg-gray-900 dark:bg-gray-100',
  cancellation: 'bg-amber-600',
};

function euro(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

function datum(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function kurzdatum(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function inMonaten(monate: number): string {
  const heute = new Date(`${todayIso()}T12:00:00`);
  heute.setMonth(heute.getMonth() + monate);
  return heute.toISOString().slice(0, 10);
}

function Kennzahl({ label, wert, hinweis }: { label: string; wert: string; hinweis: string }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs font-semibold text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums">{wert}</div>
      <div className="text-xs text-muted-foreground">{hinweis}</div>
    </div>
  );
}

/**
 * Liquiditätsprognose für Geschäftsführung und Buchhaltung: rechnet den Bestand Tag für
 * Tag bis zum Enddatum hoch, ohne etwas zu buchen. Vorlage ist Tobis Paket-Simulation;
 * es gelten die Regeln aus dem Code (Auszahlung am festen Tag, kein Deckel je Paket).
 */
export default function LiquiditaetsprognosePage() {
  const { selectedStandortId } = useStandort();
  const [bis, setBis] = useState(() => inMonaten(6));
  const [auszahltag, setAuszahltag] = useState('23');
  const [startsaldo, setStartsaldo] = useState('0');
  const [prognose, setPrognose] = useState<CashflowForecast | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [index, setIndex] = useState(0);
  const [laeuft, setLaeuft] = useState(false);
  const [tempo, setTempo] = useState(NORMALTEMPO);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const tage = useMemo(() => prognose?.days ?? [], [prognose]);
  const letzter = Math.max(0, tage.length - 1);
  const tag = tage[Math.min(index, letzter)];

  const berechnen = async () => {
    const zahltag = Number(auszahltag);
    if (!bis || !Number.isInteger(zahltag) || zahltag < 1 || zahltag > 28) {
      toast.error('Bitte ein Enddatum und einen Auszahlungstag zwischen 1 und 28 angeben.');
      return;
    }
    setLaedt(true);
    setLaeuft(false);
    try {
      const daten = await paymentsApi.cashflowForecast({
        until: bis,
        standortId: selectedStandortId ?? undefined,
        payoutDay: zahltag,
        startBalanceCents: Math.round((Number(startsaldo.replace(',', '.')) || 0) * 100),
      });
      setPrognose(daten);
      setIndex(0);
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Prognose konnte nicht berechnet werden');
    } finally {
      setLaedt(false);
    }
  };

  // Abspielen: ein Tag je Takt, am Ende anhalten.
  useEffect(() => {
    if (!laeuft) return undefined;
    timer.current = setInterval(() => {
      setIndex((i) => {
        if (i >= letzter) {
          setLaeuft(false);
          return i;
        }
        return i + 1;
      });
    }, tempo);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [laeuft, tempo, letzter]);

  const schritt = useCallback(
    (delta: number) => {
      setLaeuft(false);
      setIndex((i) => Math.max(0, Math.min(letzter, i + delta)));
    },
    [letzter],
  );

  const naechstesEreignis = () => {
    setLaeuft(false);
    const naechster = tage.findIndex((t, i) => i > index && t.events.length > 0);
    setIndex(naechster === -1 ? letzter : naechster);
  };

  const neuStarten = () => {
    setIndex(0);
    setLaeuft(true);
  };

  const tempoAendern = useCallback((richtung: number) => {
    setTempo((aktuell) => {
      const i = TEMPO.indexOf(aktuell);
      return TEMPO[Math.max(0, Math.min(TEMPO.length - 1, (i === -1 ? 4 : i) + richtung))];
    });
  }, []);

  // Leertaste: Start/Pause, +/−: Tempo — nicht in Eingabefeldern.
  useEffect(() => {
    if (!prognose) return undefined;
    const taste = (e: KeyboardEvent) => {
      const ziel = (e.target as HTMLElement | null)?.tagName ?? '';
      if (['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON'].includes(ziel)) return;
      if (e.code === 'Space') {
        e.preventDefault();
        setLaeuft((l) => !l);
      } else if (e.key === '+' || e.code === 'NumpadAdd') {
        tempoAendern(1);
      } else if (e.key === '-' || e.code === 'NumpadSubtract') {
        tempoAendern(-1);
      }
    };
    document.addEventListener('keydown', taste);
    return () => document.removeEventListener('keydown', taste);
  }, [prognose, tempoAendern]);

  const diagramm = useMemo(
    () =>
      tage.map((t) => ({
        date: t.date,
        kontostand: t.balanceCents / 100,
        verbindlichkeit: t.liabilityCents / 100,
        auszahlbar: t.payableCents / 100,
        auszahlung: t.payoutCents ? t.payoutCents / 100 : null,
      })),
    [tage],
  );

  const verlauf = useMemo(
    () =>
      tage
        .slice(0, index + 1)
        .flatMap((t) => t.events.map((e) => ({ ...e, date: t.date })))
        .reverse()
        .slice(0, 80),
    [tage, index],
  );

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Liquiditätsprognose</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Zahlungseingänge, Verbindlichkeit gegenüber Lehrkräften und Honorarauszahlungen Tag für Tag
          bis zum Enddatum, gerechnet aus dem heutigen Bestand. Es wird nichts gebucht.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Annahmen</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-4 sm:grid-cols-4 sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              void berechnen();
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="prognose-bis">Enddatum</Label>
              <Input id="prognose-bis" type="date" value={bis} min={todayIso()} onChange={(e) => setBis(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prognose-tag">Auszahlungstag</Label>
              <Input
                id="prognose-tag"
                type="number"
                min={1}
                max={28}
                value={auszahltag}
                onChange={(e) => setAuszahltag(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prognose-saldo">Kontostand heute (€)</Label>
              <Input
                id="prognose-saldo"
                inputMode="decimal"
                value={startsaldo}
                onChange={(e) => setStartsaldo(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={laedt}>
              {laedt ? 'Rechne …' : 'Prognose berechnen'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {prognose && tag ? (
        <>
          <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 p-3">
            <Button variant="outline" size="sm" onClick={neuStarten}>
              <RotateCcw className="mr-1 h-4 w-4" /> Neu starten
            </Button>
            <Button variant="outline" size="sm" onClick={() => schritt(-1)} aria-label="Tag zurück">
              <ChevronLeft className="mr-1 h-4 w-4" /> Tag zurück
            </Button>
            <Button size="sm" onClick={() => setLaeuft((l) => !l)}>
              {laeuft ? <Pause className="mr-1 h-4 w-4" /> : <Play className="mr-1 h-4 w-4" />}
              {laeuft ? 'Pause' : index > 0 ? 'Weiter' : 'Start'}
            </Button>
            <Button variant="outline" size="sm" onClick={naechstesEreignis}>
              Nächstes Ereignis <SkipForward className="ml-1 h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => schritt(1)} aria-label="Tag vor">
              1 Tag <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
            <div className="ml-2 text-xs text-muted-foreground">
              Prognosetag
              <div className="text-sm font-bold text-foreground" data-testid="prognose-tag">
                {datum(tag.date)}
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              Fortschritt
              <div className="text-sm font-bold text-foreground">
                {Math.round((index / Math.max(1, letzter)) * 100)} % · Tag {index + 1}/{tage.length}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={() => tempoAendern(-1)} aria-label="Langsamer">
                <Minus className="h-4 w-4" />
              </Button>
              <span className="min-w-[3.5rem] text-center text-sm font-semibold tabular-nums">
                {(NORMALTEMPO / tempo).toFixed(1).replace('.', ',')}×
              </span>
              <Button variant="outline" size="sm" onClick={() => tempoAendern(1)} aria-label="Schneller">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Kennzahl label="Kontostand" wert={euro(tag.balanceCents)} hinweis="Startsaldo plus Eingänge minus Auszahlungen" />
            <Kennzahl label="Zahlungseingang" wert={euro(tag.incomeTotalCents)} hinweis="seit heute" />
            <Kennzahl label="Offene Verbindlichkeit" wert={euro(tag.liabilityCents)} hinweis="noch nicht ausgezahltes Honorar" />
            <Kennzahl label="Auszahlbar" wert={euro(tag.payableCents)} hinweis="erbrachter, noch nicht ausgezahlter Unterricht" />
            <Kennzahl label="Ausgezahlt" wert={euro(tag.paidTotalCents)} hinweis="seit heute" />
          </div>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Verlauf</CardTitle>
              <span className="text-xs text-muted-foreground">
                Auszahlung am {prognose.payoutDay}. · ohne Obergrenze je Paket
              </span>
            </CardHeader>
            <CardContent>
              <div className="h-80 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={diagramm} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                    <XAxis dataKey="date" tickFormatter={kurzdatum} tick={{ fontSize: 11 }} tickLine={false} minTickGap={24} />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      tickFormatter={(v: number) => `${Math.round(v).toLocaleString('de-DE')} €`}
                      width={80}
                    />
                    <Tooltip
                      labelFormatter={(v) => datum(String(v))}
                      formatter={(v) => (typeof v === 'number' ? euro(Math.round(v * 100)) : String(v))}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="auszahlung" name="Auszahlung" fill={FARBE.auszahlung} barSize={6} />
                    <Line type="monotone" dataKey="kontostand" name="Kontostand" stroke={FARBE.kontostand} strokeWidth={2} dot={false} isAnimationActive={false} />
                    <Line type="monotone" dataKey="verbindlichkeit" name="Verbindlichkeit" stroke={FARBE.verbindlichkeit} strokeWidth={2} dot={false} isAnimationActive={false} />
                    <Line
                      type="monotone"
                      dataKey="auszahlbar"
                      name="Auszahlbar"
                      stroke={FARBE.auszahlbar}
                      strokeWidth={2}
                      strokeDasharray="5 4"
                      dot={false}
                      isAnimationActive={false}
                    />
                    <ReferenceLine x={tag.date} stroke="currentColor" strokeDasharray="4 4" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Ereignisse</CardTitle>
              </CardHeader>
              <CardContent>
                {verlauf.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Noch kein Ereignis.</p>
                ) : (
                  <ul className="divide-y" aria-label="Ereignisse">
                    {verlauf.map((e, i) => (
                      <li key={`${e.date}-${e.kind}-${e.category ?? ''}-${i}`} className="flex gap-3 py-2 text-sm">
                        <span className={`mt-1.5 h-2 w-2 flex-none rounded-full ${PUNKT[e.kind]}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap justify-between gap-2">
                            <span>
                              <b>{datum(e.date)}</b> · {e.title}
                            </span>
                            {e.amountCents !== 0 && (
                              <span className="font-semibold tabular-nums">{euro(e.amountCents)}</span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">{e.detail}</div>
                          {e.items.length > 0 && (
                            <details className="mt-1 text-xs text-muted-foreground">
                              <summary className="cursor-pointer">Einzelheiten</summary>
                              <ul className="mt-1 space-y-0.5">
                                {e.items.map((posten) => (
                                  <li key={posten.text} className="flex justify-between gap-2">
                                    <span className="truncate">{posten.text}</span>
                                    <span className="tabular-nums">{euro(posten.amountCents)}</span>
                                  </li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Bis {datum(prognose.until)}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span>Kontostand am Ende</span>
                  <b className="tabular-nums">{euro(prognose.summary.endBalanceCents)}</b>
                </div>
                <div className="flex justify-between">
                  <span>Tiefster Stand ({datum(prognose.summary.minBalanceDate)})</span>
                  <b className={`tabular-nums ${prognose.summary.minBalanceCents < 0 ? 'text-destructive' : ''}`}>
                    {euro(prognose.summary.minBalanceCents)}
                  </b>
                </div>
                <div className="flex justify-between">
                  <span>Folgepakete</span>
                  <b className="tabular-nums">{prognose.summary.followupPackages}</b>
                </div>
                <div className="flex justify-between">
                  <span>Stunden</span>
                  <b className="tabular-nums">{prognose.summary.lessonCount}</b>
                </div>
                {prognose.summary.overdueCents > 0 && (
                  <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">
                    {euro(prognose.summary.overdueCents)} sind überfällig und nicht eingerechnet.
                  </p>
                )}
                <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
                  {prognose.assumptions.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        !laedt && (
          <p className="text-sm text-muted-foreground">
            Enddatum wählen und „Prognose berechnen“ klicken. Gerechnet wird für den gewählten Standort, ohne Auswahl für alle.
          </p>
        )
      )}
    </div>
  );
}
